import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Table, PageHeader, Modal, Col, Select, Input } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { createComponentStore, useStoreState } from "easy-peasy";
import { CONTRACT_STATUS, TIME_OBJECTS, CURRENCY } from "Constants/";
import ExportExcel from "Components/export-excel";
import {
  ContractReportStoreModel,
  ACTION_TYPES,
} from "store/models/reports/contracts";
import FormLayout, { FormItem } from "Components/form";
import FetchSelect from "Components/fetch-select";
import { Controller, useForm } from "react-hook-form";
import RentableAreaSelect from "Components/rentablearea-select";
import DatePicker from "Components/date-picker-localized";
import { clean, formatCurrency, sort } from "Utils";
import PartnerSelect from "Components/partner-select";
import FourOThree from "Pages/private/403";
import { AuthCheck, AUTH_ACTIONS } from "authorization";

const useContractReportsStore = createComponentStore(ContractReportStoreModel);

export default function ContractReportsPage() {
  const [store, actions] = useContractReportsStore();
  const { fetch, search, setFilters } = actions;
  const { process, filters } = store;
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  const [data, setData] = useState([]);

  const { abilities } = useStoreState((store) => store.auth);
  const IsAuthorized = AuthCheck(
    abilities,
    AUTH_ACTIONS.READ,
    "Sözleşme Raporları"
  );

  const form = useForm({
    mode: "onSubmit",
    defaultValues: {
      typeId: [],
      partnerId: null,
      rentableAreaId: null,
      createdAt: [],
      dateOfBeginning: [],
      dateOfFinish: [],
      dateOfPlaceDelivery: [],
    },
  });

  const { handleSubmit, reset } = form;

  useEffect(() => {
    fetch({ ...filters, state: "ACTIVE" });
  }, [filters]);

  useEffect(() => {
    setData(store.data);
  }, [store.data]);

  const columns = [
    {
      title: "Kod",
      dataIndex: "code",
      width: 80,
      key: "code",
      sorter: (a, b) => a.code.substr(1) - b.code.substr(1),
    },
    {
      title: "Açıklama",
      dataIndex: "description",
      width: 200,
      key: "description",
      ellipsis: true,
      sorter: (a, b) => sort(a.description, b.description),
    },

    {
      title: "Durumu",
      dataIndex: "contractStatu",
      key: "contractStatu",
      width: 100,
      ellipsis: true,
      render: (text, record) => CONTRACT_STATUS[record.contractStatu],
      sorter: (a, b) =>
        sort(
          CONTRACT_STATUS[a.contractStatu],
          CONTRACT_STATUS[b.contractStatu]
        ),
    },

    {
      title: "Muhatap",
      dataIndex: "partner",
      key: "partner",
      width: 200,
      ellipsis: true,
      render: (text, record) =>
        (record.partner.name || "") + " " + (record.partner.surname || ""),
      sorter: (a, b) =>
        sort(
          (a.partner.name || "") + " " + (b.partner.surname || ""),
          (a.partner.name || "") + " " + (b.partner.surname || "")
        ),
    },
    {
      title: "Türü",
      dataIndex: ["type", "description"],
      key: "description",
      ellipsis: true,
      width: 130,
      sorter: (a, b) => sort(a.type.description, b.type.description),
    },
    {
      title: "Kiralanmış Alanlar",
      dataIndex: "rentableAreas",
      width: 250,
      key: "rentableAreas",
      ellipsis: true,
      render: (text, record) =>
        record.rentableAreas
          .map((item) => item.rentableArea?.description || "")
          .join(","),
    },

    {
      title: "Koşul Tipleri ve Miktarlar",
      dataIndex: "conditions",
      key: "conditions",
      ellipsis: true,
      width: 250,
      render: (text, record) =>
        record.conditions
          .map(
            (item) =>
              item.type?.description +
              "/ " +
              item.documentAmount +
              CURRENCY[item.documentCurrency]
          )
          .toString(),
    },

    {
      title: "Toplam Alacak",
      dataIndex: "totalDebt",
      key: "totalDebt",
      ellipsis: true,
      width: 150,
      sorter: (a, b) => a.totalDebt - b.totalDebt,
      render: (text, record) => formatCurrency(record.totalDebt),
    },

    {
      title: "Toplam Borç",
      dataIndex: "totalCredit",
      key: "totalCredit",
      ellipsis: true,
      width: 150,
      sorter: (a, b) => a.totalCredit - b.totalCredit,
      render: (text, record) => formatCurrency(record.totalCredit),
    },

    {
      title: "Toplam Ödeme",
      dataIndex: "totalPayment",
      key: "totalPayment",
      ellipsis: true,
      width: 150,
      sorter: (a, b) => a.totalPayment - b.totalPayment,
      render: (text, record) => {
        return formatCurrency(record.totalPayment);
      },
    },

    {
      title: "Toplam Nakit Akışı",
      dataIndex: "totalCashflow",
      key: "totalCashflow",
      ellipsis: true,
      width: 130,
      sorter: (a, b) => a.totalCashflow - b.totalCashflow,
      render: (text, record) => formatCurrency(record.totalCashflow),
    },

    {
      title: "Uzama Kuralı",
      dataIndex: ["extensionRule", "description"],
      ellipsis: true,
      width: 140,
      key: "extensionRule",
      sorter: (a, b) =>
        sort(
          a.extensionRule?.description || "",
          b.extensionRule?.description || ""
        ),
    },

    {
      title: "Hatırlatma Şekli",
      dataIndex: ["notificationType", "description"],
      key: "notificationType",
      ellipsis: true,
      width: 130,
    },

    {
      title: "Hatırlatma Zamanı",
      dataIndex: "notificationDuration",
      key: "notificationDuration",
      ellipsis: true,
      width: 130,
      render: (text, record) =>
        (record.notificationDuration || "") +
        " " +
        (TIME_OBJECTS[record.timeObject] || ""),
    },

    {
      title: "İmzalanma Tarihi",
      dataIndex: "dateOfSign",
      key: "dateOfSign",
      width: 130,
      ellipsis: true,
      sorter: (a, b) => sort(a.dateOfSign, b.dateOfSign),
      render: (text, record) => {
        return record.dateOfSign
          ? moment(record.dateOfSign).format("DD.MM.YYYY")
          : null;
      },
    },

    {
      title: "Yer Teslim Tarihi",
      dataIndex: "dateOfPlaceDelivery",
      key: "dateOfPlaceDelivery",
      width: 120,
      ellipsis: true,
      sorter: (a, b) => sort(a.dateOfPlaceDelivery, b.dateOfPlaceDelivery),
      render: (text, record) => {
        return record.dateOfPlaceDelivery
          ? moment(record.dateOfPlaceDelivery).format("DD.MM.YYYY")
          : null;
      },
    },

    {
      title: "Fesih Tarihi",
      dataIndex: "dateOfRescission",
      key: "dateOfRescission",
      width: 120,
      ellipsis: true,
      sorter: (a, b) => sort(a.dateOfRescission, b.dateOfRescission),
      render: (text, record) => {
        return record.dateOfRescission
          ? moment(record.dateOfRescission).format("DD.MM.YYYY")
          : null;
      },
    },

    {
      title: "Başlangıç Tarihi",
      dataIndex: "dateOfBeginning",
      key: "dateOfBeginning",
      width: 120,
      ellipsis: true,
      sorter: (a, b) => sort(a.dateOfBeginning, b.dateOfBeginning),
      render: (text, record) => {
        return record.dateOfBeginning
          ? moment(record.dateOfBeginning).format("DD.MM.YYYY")
          : null;
      },
    },

    {
      title: "Bitiş Tarihi",
      dataIndex: "dateOfFinish",
      key: "dateOfFinish",
      width: 120,
      ellipsis: true,
      sorter: (a, b) => sort(a.dateOfFinish, b.dateOfFinish),
      render: (text, record) => {
        return record.dateOfFinish
          ? moment(record.dateOfFinish).format("DD.MM.YYYY")
          : null;
      },
    },
  ];

  const getExcelColumns = () => {
    return columns.map((d) => d.title);
  };

  const getExcelRows = () => {
    let rows = [];
    data.forEach((d) => {
      let i = [];
      i.push(d.code || "-");
      i.push(d.description || "-");
      i.push(CONTRACT_STATUS[d.contractStatu] || "-");
      i.push((d.partner?.name || "-") + " " + (d.partner?.surname || "-"));
      i.push(d.type?.description || "-");
      i.push(
        d.rentableAreas
          .map(
            (item) =>
              (item.rentableArea?.code || "") +
              "-" +
              (item.rentableArea?.description || "")
          )
          .toString()
      );
      i.push(
        d.conditions
          .map(
            (item) =>
              (item.type?.description || "") +
              "/ " +
              (item.documentAmount || "") +
              (CURRENCY[item.documentCurrency] || "")
          )
          .toString()
      );
      i.push(formatCurrency(d.totalDebt));
      i.push(formatCurrency(d.totalCredit));
      i.push(formatCurrency(d.totalPayment));
      i.push(formatCurrency(d.totalCashflow));
      i.push(d.extensionRule?.description || "");
      i.push(d.notificationType?.description || "");
      i.push(
        (d.notificationDuration || "") +
          " " +
          (TIME_OBJECTS[d.timeObject] || "")
      );
      i.push(d.dateOfSign ? moment(d.dateOfSign).format("DD.MM.YYYY") : "");
      i.push(
        d.dateOfPlaceDelivery
          ? moment(d.dateOfPlaceDelivery).format("DD.MM.YYYY")
          : ""
      );
      i.push(
        d.dateOfRescission
          ? moment(d.dateOfRescission).format("DD.MM.YYYY")
          : ""
      );
      i.push(
        d.dateOfBeginning ? moment(d.dateOfBeginning).format("DD.MM.YYYY") : ""
      );
      i.push(d.dateOfFinish ? moment(d.dateOfFinish).format("DD.MM.YYYY") : "");
      rows.push(i);
    });

    return rows;
  };

  const handleAdvancedSearchOK = (form) => {
    let filterData = { ...form };

    delete filterData.createdAt;
    if (form.createdAt.length > 0) {
      filterData.createdAtBegin = moment(form.createdAt[0]).format(
        "yyyy-MM-DDT00:00:00"
      );
      filterData.createdAtAfter = moment(form.createdAt[1]).format(
        "yyyy-MM-DDT00:00:00"
      );
    }

    delete filterData.dateOfBeginning;
    if (form.dateOfBeginning.length > 0) {
      filterData.dOBBegin = moment(form.dateOfBeginning[0]).format(
        "yyyy-MM-DDT00:00:00"
      );
      filterData.dOBAfter = moment(form.dateOfBeginning[1]).format(
        "yyyy-MM-DDT00:00:00"
      );
    }

    delete filterData.dateOfFinish;
    if (form.dateOfFinish.length > 0) {
      filterData.dOFBegin = moment(form.dateOfFinish[0]).format(
        "yyyy-MM-DDT00:00:00"
      );
      filterData.dOFAfter = moment(form.dateOfFinish[1]).format(
        "yyyy-MM-DDT00:00:00"
      );
    }

    delete filterData.dateOfPlaceDelivery;
    if (form.dateOfPlaceDelivery.length > 0) {
      filterData.dOPBegin = moment(form.dateOfPlaceDelivery[0]).format(
        "yyyy-MM-DDT00:00:00"
      );
      filterData.dOPAfter = moment(form.dateOfPlaceDelivery[1]).format(
        "yyyy-MM-DDT00:00:00"
      );
    }

    clean(filterData);
    setFilters(filterData);
    setFilterModalVisible(false);
  };

  const handleAdvancedSearchCancel = () => {
    reset();
    setFilters({});
  };

  return IsAuthorized ? (
    <>
      <PageHeader
        title="Sözleşme Raporları"
        extra={[
          <Button
            key="filter"
            onClick={() => {
              setFilterModalVisible(true);
            }}
          >
            <FontAwesomeIcon
              icon={["fas", "filter"]}
              style={{ marginRight: 8 }}
            />
            Detaylı Arama
          </Button>,
          <ExportExcel
            columnsData={getExcelColumns()}
            key={data}
            disabled={data.length < 1}
            rowsData={getExcelRows()}
            loading={store.process === ACTION_TYPES.FETCHING_DATA}
            filename={
              "Sözleşme Raporları_" + moment(new Date()).format("DD-MM-YYYY")
            }
          />,
        ]}
      />
      <Table
        style={{ margin: "0 24px" }}
        rowKey={(row) => row.id}
        loading={process === ACTION_TYPES.FETCHING_DATA}
        dataSource={data}
        columns={columns}
        size="small"
        bordered
        showSorterTooltip={false}
        scroll={{ x: 1000, scrollToFirstRowOnChange: true }}
        pagination={{
          pageSize: 20,
          total: data.length,
          showSizeChanger: false,
          showTotal: (total, range) => {
            return `Toplam ${total} sonuçtan ${range.join(
              "-"
            )} arası gösteriliyor.`;
          },
        }}
      />
      <Modal
        title="Detaylı Arama"
        centered
        maskClosable={false}
        visible={filterModalVisible}
        onOk={handleSubmit(handleAdvancedSearchOK)}
        onCancel={() => setFilterModalVisible(false)}
        footer={[
          <Button key="clear" onClick={handleAdvancedSearchCancel}>
            Temizle
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit(handleAdvancedSearchOK)}
          >
            <FontAwesomeIcon
              icon={["fas", "search"]}
              style={{ marginRight: 8 }}
            />
            Ara
          </Button>,
        ]}
        width="60vw"
      >
        <FormLayout>
          <Col xs={24} md={12}>
            <FormItem label="Hızlı Arama">
              <Controller
                name="fastSearchParam"
                control={form.control}
                as={<Input placeholder="Kod/Açıklama" />}
              />
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem label="Sözleşme Durumu">
              <Controller
                control={form.control}
                name="statu"
                as={
                  <Select>
                    {Object.keys(CONTRACT_STATUS).map((d) => (
                      <Select.Option value={d} key={d}>
                        {CONTRACT_STATUS[d]}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem label="Sözleşme Türü">
              <FetchSelect
                type="multiple"
                api="/masterdata/contracttypes"
                form={form}
                field="typeId"
                placeholder="Sözleşme Türü"
                objectInValue={false}
              />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem label="Kiracı/Öğrenci">
              <PartnerSelect
                form={form}
                objectInValue={false}
                allowClear={true}
                field="partnerId"
                style={{ width: "100%" }}
              />
            </FormItem>
          </Col>
          <Col xs={24}>
            <FormItem label="Kiralanabilir Alan">
              <RentableAreaSelect
                form={form}
                field="rentableAreaId"
                objectInValue={false}
              />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem label="Başlangıç Tarihi">
              <Controller
                name="dateOfBeginning"
                control={form.control}
                as={
                  <DatePicker
                    isRangePicker={true}
                    format="DD.MM.YYYY"
                    style={{ width: "100%" }}
                  />
                }
              />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem label="Bitiş Tarihi">
              <Controller
                name="dateOfFinish"
                control={form.control}
                as={
                  <DatePicker
                    isRangePicker={true}
                    format="DD.MM.YYYY"
                    style={{ width: "100%" }}
                  />
                }
              />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem label="Oluşturma Tarihi">
              <Controller
                name="createdAt"
                control={form.control}
                as={
                  <DatePicker
                    isRangePicker={true}
                    format="DD.MM.YYYY"
                    style={{ width: "100%" }}
                  />
                }
              />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem label="Yer Teslim Tarihi">
              <Controller
                name="dateOfPlaceDelivery"
                control={form.control}
                as={
                  <DatePicker
                    isRangePicker={true}
                    format="DD.MM.YYYY"
                    style={{ width: "100%" }}
                  />
                }
              />
            </FormItem>
          </Col>
        </FormLayout>
      </Modal>
    </>
  ) : (
    <FourOThree />
  );
}
